import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Check, Minus, Minus as MinusIcon, Plus as PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { analytics } from "@heycatch/sdk";

const SEAT_PRICE = 24;
const SEAT_PRICE_ANNUAL = 20;
const MIN_SEATS = 5;

const TIERS = [
  {
    id: "personal",
    name: "Personal",
    tagline: "Freelancers & curious evaluators",
    priceAnnual: "$0",
    priceMonthly: "$0",
    suffix: "/ month",
    cta: "Start free",
    kind: "free",
    highlight: false,
  },
  {
    id: "personal-pro",
    name: "Personal Pro",
    tagline: "Solo PMs & consultants",
    priceAnnual: "$8",
    priceMonthly: "$10",
    suffix: "/ month",
    cta: "Start 14-day trial",
    kind: "stripe",
    highlight: true,
    ribbon: "Most popular",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Teams & PMO cores",
    priceAnnual: `$${SEAT_PRICE_ANNUAL}`,
    priceMonthly: `$${SEAT_PRICE}`,
    suffix: "/ seat / mo",
    noteAnnual: `Billed annually · ${MIN_SEATS} seat min · save $4/seat`,
    noteMonthly: `Billed monthly · ${MIN_SEATS} seat min`,
    cta: "Buy seats",
    kind: "enterprise",
    highlight: false,
  },
  {
    id: "enterprise-pro",
    name: "Enterprise Pro",
    tagline: "Fortune 1000 & VPMOs",
    priceAnnual: "Custom",
    priceMonthly: "Custom",
    suffix: "",
    note: "From ~$20 / seat / mo",
    cta: "Talk to us",
    kind: "sales",
    highlight: false,
  },
];

const Y = true, N = false;
const COMPARISON = [
  { group: "Planning essentials", rows: [
    { label: "Active projects", vals: ["Up to 3", "Unlimited", "Unlimited", "Unlimited"] },
    { label: "Tasks per project", vals: ["Up to 75", "Unlimited", "Unlimited", "Unlimited"] },
    { label: "Gantt timeline & dependencies", vals: [Y, Y, Y, Y] },
    { label: "Critical path highlighting", vals: [N, Y, Y, Y] },
    { label: "Baseline snapshots & variance", vals: [N, Y, Y, Y] },
    { label: "Reusable project templates", vals: [N, Y, Y, Y] },
  ]},
  { group: "Risk & governance", rows: [
    { label: "Risk Register per project", vals: [Y, Y, Y, Y] },
    { label: "Risk Library (reusable templates)", vals: ["Starter pack only", Y, Y, Y] },
    { label: "CSV bulk import (tasks & risks)", vals: [N, Y, Y, Y] },
    { label: "Project risk score (0-100)", vals: [Y, Y, Y, Y] },
    { label: "Audit log", vals: [N, Y, Y, Y] },
  ]},
  { group: "AI copilots", rows: [
    { label: "AI prompts per month", vals: ["10", "Unlimited", "Unlimited", "Unlimited + priority"] },
    { label: "Executive summary + tone picker", vals: [N, Y, Y, Y] },
    { label: "AI-suggested risks", vals: [N, Y, Y, Y] },
    { label: "AI Mitigation Coach", vals: [N, Y, Y, Y] },
  ]},
  { group: "Reporting & delivery", rows: [
    { label: "Branded PDF status pack", vals: [N, Y, Y, Y] },
    { label: "Monday weekly email digest", vals: [N, Y, Y, Y] },
    { label: "Portfolio & program rollups", vals: [N, N, Y, Y] },
    { label: "Multi-currency", vals: [N, Y, Y, Y] },
    { label: "Resource load & over-booking alerts", vals: [N, Y, Y, Y] },
  ]},
  { group: "Collaboration & brand", rows: [
    { label: "Teammate seats", vals: ["—", "1", "5–500 self-serve", "Unlimited"] },
    { label: "Public client portal + white-label", vals: [N, Y, Y, Y] },
    { label: "Opt-in Top risks on client portal", vals: [N, Y, Y, Y] },
  ]},
  { group: "Platform", rows: [
    { label: "Web + iOS + Android", vals: [Y, Y, Y, Y] },
    { label: "Priority email support", vals: [N, Y, Y, Y] },
    { label: "Dedicated CSM & SLA", vals: [N, N, N, Y] },
    { label: "SSO / SAML", vals: [N, N, "Add-on", Y] },
    { label: "Custom data retention & export", vals: [N, N, N, Y] },
  ]},
];

function cellRender(v) {
  if (v === true) return <Check size={16} className="text-emerald-400 mx-auto"/>;
  if (v === false) return <Minus size={14} className="text-slate-600 mx-auto"/>;
  return <span className="text-xs text-slate-200">{v}</span>;
}

export default function Pricing() {
  const [busy, setBusy] = useState(null);
  const [annual, setAnnual] = useState(true);
  const [seats, setSeats] = useState(MIN_SEATS);
  const enterpriseTotal = useMemo(() => {
    const perSeat = annual ? SEAT_PRICE_ANNUAL : SEAT_PRICE;
    return { monthly: seats * perSeat, perSeat };
  }, [seats, annual]);

  const startCheckout = async (planKey, quantity = 1) => {
    setBusy(planKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        plan: planKey,
        seats: quantity,
      });
      analytics.trackEvent("checkout_started", { plan: planKey, seats: quantity, billing: annual ? "annual" : "monthly" });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };

  const handleCTA = (tier) => {
    if (tier.kind === "free") { window.location.href = "/register"; return; }
    if (tier.kind === "sales") { window.location.href = "mailto:sales@upliftc.com?subject=Compass%20PM%20Enterprise%20Pro"; return; }
    if (tier.kind === "stripe") {
      startCheckout(annual ? "personal_pro_annual" : "personal_pro_monthly", 1);
      return;
    }
    if (tier.kind === "enterprise") {
      startCheckout(annual ? "enterprise_annual" : "enterprise", seats);
    }
  };

  return (
    <div className="min-h-screen pc-grid-bg py-16 px-4 sm:px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/5 text-xs uppercase tracking-widest text-blue-300 mb-5">
            Simple pricing · Cancel anytime
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" style={{ fontFamily: "Outfit" }}>Pick the plan that fits where you are</h1>
          <p className="text-slate-400 max-w-2xl mx-auto">Start free, upgrade when the plan outgrows the spreadsheet. Enterprise self-serves 5–500 seats; Enterprise Pro adds SSO and a dedicated CSM.</p>
          <div className="mt-6 inline-flex rounded-lg border border-slate-700 overflow-hidden" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} data-testid="toggle-monthly" className={`px-4 py-2 text-sm ${!annual ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="toggle-annual" className={`px-4 py-2 text-sm ${annual ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>Annual · <span className="text-emerald-300">save 20%</span></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {TIERS.map((t) => (
            <div key={t.id} data-testid={`tier-card-${t.id}`}
              className={`pc-card p-6 relative ${t.highlight ? "border-blue-500/60 shadow-xl shadow-blue-900/30 ring-1 ring-blue-500/30" : ""}`}>
              {t.ribbon && (
                <div className="absolute -top-3 left-6 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded">{t.ribbon}</div>
              )}
              <div className="text-sm font-semibold text-blue-300 mb-1" style={{ fontFamily: "Outfit" }}>{t.name}</div>
              <div className="text-xs text-slate-400 mb-4 min-h-[2em]">{t.tagline}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{annual ? t.priceAnnual : t.priceMonthly}</span>
                {t.suffix && <span className="text-xs text-slate-400">{t.suffix}</span>}
              </div>
              <div className="text-[11px] text-slate-500 mb-4 min-h-[1.5em]">{(annual ? t.noteAnnual : t.noteMonthly) || t.note || (t.id === "personal-pro" ? (annual ? "Billed annually · 14-day trial" : "Billed monthly · 14-day trial") : "")}</div>

              {t.kind === "enterprise" && (
                <div className="mb-4 rounded-lg border border-slate-700 bg-slate-900/60 p-3" data-testid="enterprise-seat-picker">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Seats</div>
                  <div className="flex items-center justify-between">
                    <button data-testid="seats-decrement" onClick={() => setSeats((s) => Math.max(MIN_SEATS, s - 1))}
                      className="w-7 h-7 rounded-md border border-slate-700 text-slate-200 hover:border-blue-400 flex items-center justify-center disabled:opacity-40"
                      disabled={seats <= MIN_SEATS}>
                      <MinusIcon size={12}/>
                    </button>
                    <input type="number" min={MIN_SEATS} max={500} value={seats}
                      onChange={(e) => setSeats(Math.max(MIN_SEATS, Math.min(500, Number(e.target.value) || MIN_SEATS)))}
                      data-testid="seats-input"
                      className="w-16 text-center bg-transparent text-white text-lg font-semibold outline-none" />
                    <button data-testid="seats-increment" onClick={() => setSeats((s) => Math.min(500, s + 1))}
                      className="w-7 h-7 rounded-md border border-slate-700 text-slate-200 hover:border-blue-400 flex items-center justify-center">
                      <PlusIcon size={12}/>
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-2 text-center" data-testid="enterprise-total">
                    {seats} × ${enterpriseTotal.perSeat.toFixed(2)} = <strong className="text-white">${enterpriseTotal.monthly.toFixed(2)}/mo</strong>
                    {annual && <div className="text-[10px] text-emerald-300 mt-0.5">billed annually at ${(enterpriseTotal.monthly * 12).toFixed(2)}/yr</div>}
                  </div>
                </div>
              )}

              <button onClick={() => handleCTA(t)} disabled={busy === t.kind}
                data-testid={`cta-${t.id}`}
                className={`w-full py-2.5 rounded-lg text-sm font-semibold ${t.highlight ? "bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-400 hover:to-blue-600" : "border border-slate-700 text-slate-200 hover:border-blue-400"} disabled:opacity-60`}>
                {busy === t.kind ? "Redirecting…" : t.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="pc-card overflow-x-auto" data-testid="comparison-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/40">
                <th className="text-left p-4 text-xs uppercase tracking-widest text-slate-400 w-[32%]">Compare plans</th>
                {TIERS.map((t) => (
                  <th key={t.id} className="p-4 text-center text-xs uppercase tracking-widest text-blue-300/80">{t.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((grp) => (
                <React.Fragment key={grp.group}>
                  <tr className="bg-slate-900/20"><td colSpan={5} className="p-3 text-[10px] uppercase tracking-widest text-slate-500 border-t border-slate-800">{grp.group}</td></tr>
                  {grp.rows.map((r) => (
                    <tr key={`${grp.group}-${r.label}`} className="border-t border-slate-900/60 hover:bg-slate-900/30">
                      <td className="p-3 text-slate-300">{r.label}</td>
                      {r.vals.map((v, j) => <td key={`${r.label}-${TIERS[j].id}`} className="p-3 text-center">{cellRender(v)}</td>)}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">Powered by Stripe · Test card 4242 4242 4242 4242 · All prices in USD · 14-day free trial on paid plans</p>
        <div className="mt-6 text-center">
          <Link to="/app" data-testid="back-to-app" className="text-sm text-slate-400 hover:text-white">← Back to app</Link>
        </div>
      </div>
    </div>
  );
}
